import { useLocation } from 'react-router-dom';
import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Mail, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Banner shown to authenticated users whose email is not yet verified.
 * Dismissible per-session; permanently dismissed once verified.
 */
export default function VerifyEmailBanner() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('dca_verify_dismissed') === '1');

  const { pathname } = useLocation();
  if (!user || user.email_verified || dismissed || pathname.startsWith('/challenge')) return null;

  const resend = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/auth/send-verification`, {}, { withCredentials: true });
      toast.success('Verification link sent - check your inbox');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send verification email');
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    sessionStorage.setItem('dca_verify_dismissed', '1');
    setDismissed(true);
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 text-sm" data-testid="verify-email-banner">
      <div className="container-custom flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-amber-900">
          <Mail size={14} />
          <span><strong>Confirm your email.</strong> We sent a link to {user.email}. Some features stay locked until you verify.</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={resend} disabled={busy} className="text-xs font-bold text-amber-900 underline hover:no-underline disabled:opacity-50" data-testid="verify-email-resend">
            {busy ? 'Sending…' : 'Resend link'}
          </button>
          <Link to="/portal/settings" className="text-xs font-bold text-amber-900 underline hover:no-underline">Go to Settings</Link>
          <button onClick={close} className="text-amber-700 hover:text-amber-900" aria-label="Dismiss" data-testid="verify-email-dismiss">
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
